import axios from 'axios';
import { Contexture } from '../services/Contexture/Contexture';

// Track ongoing authentication to prevent multiple simultaneous auth attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to re-authenticate using stored user credentials
const refreshAuthentication = async () => {
  try {
    // Get stored user credentials from localStorage or sessionStorage
    // This assumes the user data is stored during initial authentication
    const storedAuth = localStorage.getItem('contexture_auth');
    if (!storedAuth) {
      throw new Error('No stored authentication data found');
    }

    const authData = JSON.parse(storedAuth);

    // Re-authenticate with Contexture service
    const response = await Contexture.authenticate({
      email: authData.email,
      organization: authData.organization || 'Ecropolis',
      given_name: authData.given_name,
      family_name: authData.family_name,
    });

    if (response && response.token) {
      return response.token;
    } else {
      throw new Error('Re-authentication failed - no token received');
    }
  } catch (error) {
    console.error('Re-authentication error:', error);
    throw error;
  }
};

// Response interceptor to handle 403 errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 403 and we haven't already tried to refresh
    if (error.response?.status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAuthentication();

        // Update the token in Contexture service
        Contexture.setToken(newToken);

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Process any queued requests
        processQueue(null, newToken);

        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        console.error('Re-authentication failed:', refreshError);

        // Process queued requests with error
        processQueue(refreshError, null);

        // Clear stored auth data if re-authentication fails
        localStorage.removeItem('contexture_auth');

        // Redirect to login or handle authentication failure
        // This could trigger a full app re-authentication flow
        // Dispatch event to trigger the OIDC re-authentication flow
        window.dispatchEvent(new CustomEvent('auth-failure'));

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Request interceptor to ensure all requests have the current token
axios.interceptors.request.use(
  (config) => {
    const token = Contexture.getToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axios;
